/**
 * The building geometry the console cannot otherwise see, and how it admits
 * to being out of date.
 *
 * WHY AN ASSET AT ALL
 * `rmf_site_map_server` builds every level with `wall_graph: Default::default()`
 * and `places: vec![]`, so `BuildingMapSnapshot.walls` is always empty on the
 * wire and the elevation the ROS message already carries is never projected.
 * Until that node is patched, wall geometry and floor outlines have to come
 * from the site file, exported offline by `scripts/twin/export.sh`.
 *
 * WHAT THE ASSET IS (schema 2.0)
 * Cut from the site image the cluster runs, by digest, and it says so
 * (`generated_from`). Six lift cars, each with its floors kept apart:
 * `served_levels` (the lift controller), `map_levels` (the robot map's cabin
 * vertices) and `robot.levels` (where robots may use it). There is
 * deliberately no single `levels` list on a car: every consumer has to say
 * which of the three it means. 1.x had one, and drew a shaft over the floors
 * the robot map visits, which is not the building.
 *
 * WHY THE STALENESS CHECK IS ON PLACES AND NOT ON THE FLOORPLAN
 * The obvious check -- compare the floorplan raster's hash, or its affine --
 * is the one that does not work here. Every map revision this site has
 * actually shipped moved anchors while leaving the rasters and drawing poses
 * byte-identical, so a hash comparison would report "exact" while the walls
 * came from the previous site file. The places set moves when the map is
 * re-authored and it is on the wire (`usePlaces()`). The asset carries it as
 * per-level digests (`places_check`), never as names.
 */

/** RMF frame units throughout. Never metres -- the frame's absolute scale
 *  against real metres is an open question on this site. */
export interface TwinLevel {
  readonly name: string;
  readonly elevation: number;
  readonly commissioned: boolean;
  /** Flat runs of `[x1, y1, x2, y2]`. */
  readonly walls: readonly (readonly number[])[];
  /** Doors, as `[x1, y1, x2, y2]` with the name the live state is keyed by.
   *
   *  Geometry lives HERE and not on the wire: the live door category is
   *  state-only by design (`adaptV1DoorState` sets `level: ''` and leaves
   *  the endpoints unset), and the registry's view model drops the yaw and
   *  width that would be needed to rebuild them. A door is an anchor pair
   *  in the site file exactly like a wall, so the exporter resolves it the
   *  same way. */
  readonly doors?: readonly { readonly name: string; readonly seg: readonly number[] }[];
  /** The floor plate ring. */
  readonly outline: readonly (readonly [number, number])[];
  readonly outline_source: string;
  /** Present only where the plate was borrowed: this level's OWN traced wall
   *  loop, which is real and is the right shape for an interior view even
   *  though it is too small to be a plate. L6's is 35% of a floor plate. */
  readonly core_ring?: readonly (readonly [number, number])[];
  readonly floorplan?: {
    readonly source: string;
    readonly sha256?: string;
    readonly expected_affine: {
      readonly translation_x: number;
      readonly translation_y: number;
      readonly rotation_rad: number;
      readonly scale: number;
    };
  };
  readonly slam?: {
    readonly asset: string;
    readonly sha256?: string;
    readonly width: number;
    readonly height: number;
    readonly pixels_per_meter: number;
    readonly translation_x: number;
    readonly translation_y: number;
    readonly yaw_deg: number;
  };
}

export interface TwinFacadeBand {
  readonly name: string;
  readonly levels: readonly string[];
  readonly ring_from: string;
  readonly ring: readonly (readonly [number, number])[];
  readonly z0: number;
  readonly z1: number;
  readonly max_overshoot_pct: number;
}

/** Where a car's box is drawn. `map` cars come from the served nav graph's
 *  `lifts{}` block (rmf_site centres); `hand` cars were placed by hand from
 *  the floorplans and carry how far that placement may be off. */
export interface TwinLiftGeometry {
  readonly source: 'map' | 'hand';
  readonly x: number;
  readonly y: number;
  /** Counter-clockwise, in the RMF frame. `depth` runs along this heading. */
  readonly yaw_rad: number;
  readonly depth: number;
  readonly width: number;
  readonly precision_units?: number;
}

/** What robots may do with a car. `none` is "not a robot lift". */
export type TwinRobotUse = 'between_floors' | 'same_floor' | 'none';

export interface TwinLift {
  readonly name: string;
  readonly geometry: TwinLiftGeometry;
  /** The floors the lift CONTROLLER says this car serves. Building truth:
   *  what a guest can ride to. */
  readonly served_levels: readonly string[];
  readonly controller: { readonly group: string; readonly car: string };
  /** The floors the ROBOT MAP has a cabin vertex on. Union of every door's
   *  visits: FL1 is a through-cabin whose second door visits L19M alone. */
  readonly map_levels: readonly string[];
  /** The map floors a lane actually reaches. */
  readonly map_lane_levels: readonly string[];
  readonly door_levels: readonly (readonly string[])[];
  readonly robot: {
    readonly use: TwinRobotUse;
    readonly levels: readonly string[];
    readonly source: string;
    /** Present while the robot map still has lanes into a car robots are
     *  not to use (decision 2). Its own words, e.g. "robot map has cabin
     *  lanes on L3, L4, L6". Gone once a map drop removes those lanes. */
    readonly map_note?: string;
  };
}

/** A git-held input the asset was cut from. */
export interface TwinSourceRef {
  readonly repo: string;
  readonly ref: string;
  readonly commit: string;
  readonly path: string;
  readonly sha256: string;
}

/** One level's waypoints, as `nx-places/1` digests them. */
export interface TwinPlacesLevelRecord {
  readonly count: number;
  readonly zones: number;
  /** FNV-1a-32 over the names sorted by code point, each followed by "\n". */
  readonly names_digest: string;
  readonly sum_x: number;
  readonly sum_y: number;
}

export interface TwinPlacesCheck {
  readonly form: string;
  /** Coordinate-sum tolerance for "positions differ", in RMF units. */
  readonly tolerance: number;
  readonly total: number;
  readonly levels: Readonly<Record<string, TwinPlacesLevelRecord>>;
}

export interface TwinGeometry {
  readonly schema_version: string;
  readonly units: string;
  readonly site: string;
  readonly generated_from: {
    readonly site_image: {
      readonly ref: string;
      readonly digest: string;
      readonly version: string;
      readonly revision: string;
    };
    readonly site_file: string;
    readonly site_file_sha256: string;
    readonly nav_graph: { readonly path: string; readonly sha256: string };
    readonly launch_file: { readonly name: string; readonly sha256: string };
    readonly site_tree: { readonly repo: string; readonly commit: string; readonly path: string };
    readonly descriptor: TwinSourceRef;
    readonly lift_roster: TwinSourceRef;
    readonly overlay: TwinSourceRef;
    readonly survey_date: string | null;
    readonly map_name: string;
    readonly map_version: string;
  };
  readonly assumptions: {
    readonly wall_thickness: number;
    readonly wall_height_default: number;
    readonly note: string;
  };
  readonly levels: readonly TwinLevel[];
  readonly facade_bands: readonly TwinFacadeBand[];
  readonly lifts: readonly TwinLift[];
  readonly places_check: TwinPlacesCheck;
}

// ---------------------------------------------------------------------------
// The waypoint check (nx-places/1).
//
// The TypeScript twin of `places_check` / `compare_places` in
// `scripts/twin/export_geometry.py`. Both are held to one golden fixture,
// `tests/fixtures/twin/places-canonical.json`, so the asset's digest and the
// console's can never quietly disagree.
//
// NAMES ONLY IN THE DIGEST. A digest over coordinates has to fix how a float
// prints, and the two languages do not agree: Python writes 31.0 where
// JavaScript writes 31, and the wire carries float32. Positions are compared
// as per-level sums with a tolerance instead. Synchronous and dependency-free
// on purpose: `crypto.subtle` does not exist on a console served over plain
// http on the site LAN, and the 1.x fingerprint used it.
// ---------------------------------------------------------------------------

export const PLACES_FORM = 'nx-places/1';
/** A 6 mm move of one waypoint reads as a difference; float32 noise on the
 *  wire (~2e-6 per coordinate) never does. */
export const PLACES_TOLERANCE = 0.005;

const FNV32_OFFSET = 0x811c9dc5;
const FNV32_PRIME = 0x01000193;

export function fnv1a32(bytes: Uint8Array): number {
  let h = FNV32_OFFSET;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, FNV32_PRIME) >>> 0;
  }
  return h >>> 0;
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    if (a[i] !== b[i]) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

/** FNV-1a-32 over the names sorted by code point, each followed by a newline,
 *  as 8 lower-case hex digits.
 *
 *  Sorted as UTF-8 BYTES, which is code-point order -- what Python's
 *  `sorted()` does. Not `localeCompare`, which puts `lobby-east` before
 *  `Phantas_charger`, and not plain `sort()`, which orders UTF-16 code units
 *  and so differs from code points above the BMP. */
export function namesDigest(names: Iterable<string>): string {
  const enc = new TextEncoder();
  const sorted = Array.from(names, (n) => enc.encode(n)).sort(compareBytes);
  const parts: number[] = [];
  for (const name of sorted) {
    for (const b of name) parts.push(b);
    parts.push(0x0a);
  }
  return fnv1a32(Uint8Array.from(parts)).toString(16).padStart(8, '0');
}

/** The fields of a waypoint the check reads. The wire's `PlaceRegistry`
 *  carries `is_cleaning_zone` as optional, for an older worker; absent reads
 *  as "not a zone", so such a worker reads as "zones differ" on every floor
 *  that has zones. Only this side meets the absence: the exporter's places
 *  always carry the flag (`export_geometry.py`), which is why its
 *  `places_level_record` indexes it outright. */
export interface PlaceLike {
  readonly name: string;
  readonly level: string;
  readonly x: number;
  readonly y: number;
  readonly is_cleaning_zone?: boolean;
}

/** Python's `round(v, 4)` for the values this sees. The two can disagree
 *  only on an exact half at the fifth decimal, which is 1/100th of the
 *  tolerance the sums are compared with. */
function round4(v: number): number {
  return Math.round(v * 1e4) / 1e4;
}

export function placesLevelRecord(places: readonly PlaceLike[]): TwinPlacesLevelRecord {
  let sumX = 0;
  let sumY = 0;
  let zones = 0;
  for (const p of places) {
    sumX += p.x;
    sumY += p.y;
    if (p.is_cleaning_zone === true) zones += 1;
  }
  return {
    count: places.length,
    zones,
    names_digest: namesDigest(places.map((p) => p.name)),
    sum_x: round4(sumX),
    sum_y: round4(sumY),
  };
}

/** The asset-side record (`places_check`) for these places, with a record
 *  for every level named, so a waypoint appearing on a level that had none
 *  still reads as a difference. */
export function placesDigest(
  places: readonly PlaceLike[],
  levels: readonly string[] = [],
): TwinPlacesCheck {
  const byLevel = new Map<string, PlaceLike[]>(levels.map((lv) => [lv, []]));
  for (const p of places) {
    const rows = byLevel.get(p.level);
    if (rows) rows.push(p);
    else byLevel.set(p.level, [p]);
  }
  const out: Record<string, TwinPlacesLevelRecord> = {};
  for (const [lv, rows] of byLevel) out[lv] = placesLevelRecord(rows);
  return { form: PLACES_FORM, tolerance: PLACES_TOLERANCE, total: places.length, levels: out };
}

export type PlacesComparison =
  | { readonly verdict: 'match' }
  | { readonly verdict: 'differ'; readonly levels: Readonly<Record<string, readonly string[]>> }
  | { readonly verdict: 'cant_check'; readonly reason: 'no places' | 'a place has no level' };

/**
 * The asset's waypoint record against the wire's places.
 *
 * Never "match" on an empty set, nor when any row has no level -- the worker
 * leaves `level` '' until `/map` arrives (`place_registry.py`), and a place
 * it cannot attribute could hide a difference anywhere.
 */
export function comparePlaces(
  check: TwinPlacesCheck,
  live: readonly PlaceLike[],
): PlacesComparison {
  if (live.length === 0) return { verdict: 'cant_check', reason: 'no places' };
  if (live.some((p) => !p.level)) return { verdict: 'cant_check', reason: 'a place has no level' };
  const tol = check.tolerance ?? PLACES_TOLERANCE;
  const byLevel = new Map<string, PlaceLike[]>();
  for (const p of live) {
    const rows = byLevel.get(p.level);
    if (rows) rows.push(p);
    else byLevel.set(p.level, [p]);
  }
  const empty = placesLevelRecord([]);
  const differs: Record<string, string[]> = {};
  for (const level of new Set([...Object.keys(check.levels), ...byLevel.keys()])) {
    const a = check.levels[level] ?? empty;
    const b = placesLevelRecord(byLevel.get(level) ?? []);
    if (a.count !== b.count) {
      const d = b.count - a.count;
      differs[level] = [`count ${d > 0 ? '+' : ''}${d}`];
      continue;
    }
    const reasons: string[] = [];
    if (a.names_digest !== b.names_digest) reasons.push('names differ');
    if (Math.abs(a.sum_x - b.sum_x) > tol || Math.abs(a.sum_y - b.sum_y) > tol) {
      reasons.push('positions differ');
    }
    if (a.zones !== b.zones) reasons.push('zones differ');
    if (reasons.length) differs[level] = reasons;
  }
  return Object.keys(differs).length
    ? { verdict: 'differ', levels: differs }
    : { verdict: 'match' };
}

// ---------------------------------------------------------------------------
// The waypoint banner (9a).
// ---------------------------------------------------------------------------

export interface WaypointBanner {
  readonly status: 'match' | 'differ' | 'cant_check';
  /** One line, the banner's title. */
  readonly text: string;
  /** Why, for the two states that are not a match. */
  readonly detail: string | null;
}

/** Natural floor order, so L9 reads before L12. */
function byFloor(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * What the banner says about the asset's waypoints against the running map.
 *
 * It names WAYPOINTS and nothing else. The comparison is over named waypoints
 * only: it says nothing about walls, outlines or the drawing, so the banner
 * may not either. "Can't check" is never dressed as a match.
 */
export function waypointBanner(
  geometry: TwinGeometry,
  live: { readonly rows: readonly PlaceLike[] | null | undefined; readonly errored: boolean },
): WaypointBanner {
  if (live.errored || !live.rows) {
    return {
      status: 'cant_check',
      text: "Waypoints: can't check",
      detail: "The running map's waypoints could not be read.",
    };
  }
  const result = comparePlaces(geometry.places_check, live.rows);
  if (result.verdict === 'cant_check') {
    return {
      status: 'cant_check',
      text: "Waypoints: can't check",
      detail:
        result.reason === 'no places'
          ? 'The running map listed no waypoints.'
          : 'A waypoint has no floor yet, so the floors cannot be compared.',
    };
  }
  const version = geometry.generated_from.map_version;
  if (result.verdict === 'match') {
    return { status: 'match', text: `Waypoints match map ${version}`, detail: null };
  }
  // Levels that differ for the same reasons are named together, so the common
  // case reads "Waypoints differ on L12, L14 (positions differ)" and a mixed
  // one still says which reason belongs to which floor.
  const groups = new Map<string, string[]>();
  for (const level of Object.keys(result.levels).sort(byFloor)) {
    const why = (result.levels[level] ?? []).join(', ');
    const floors = groups.get(why);
    if (floors) floors.push(level);
    else groups.set(why, [level]);
  }
  const parts = [...groups].map(([why, floors]) => `${floors.join(', ')} (${why})`);
  return {
    status: 'differ',
    text: `Waypoints differ on ${parts.join('; ')}`,
    detail: `This building model is from map ${version}.`,
  };
}

export function levelByName(geometry: TwinGeometry | null, name: string | null): TwinLevel | null {
  if (!geometry || !name) return null;
  return geometry.levels.find((l) => l.name === name) ?? null;
}

/**
 * Telemetry says `L6`; waypoints are named `L06-GLL`. Joining the two without
 * padding fails silently -- nothing throws, the lookup just never matches.
 */
export function toZeroPaddedLevel(level: string): string {
  const m = /^L(\d+)(M?)$/.exec(level);
  if (!m) return level;
  return `L${(m[1] ?? '').padStart(2, '0')}${m[2] ?? ''}`;
}

// ---------------------------------------------------------------------------
// The loader.
// ---------------------------------------------------------------------------

/** The asset is not one this console can draw for this site: another schema
 *  major, another unit, or another site's building. The screen says "No
 *  building model" rather than drawing the wrong one. */
export class GeometryMismatch extends Error {
  constructor(
    readonly url: string,
    readonly reason: string,
  ) {
    super(`geometry asset ${url}: ${reason}`);
    this.name = 'GeometryMismatch';
  }
}

export const GEOMETRY_SCHEMA_MAJOR = 2;
export const GEOMETRY_UNITS = 'rmf_frame';

/** Shape checks that hold whoever asks. The SITE check is per caller (below),
 *  so one wrong-site request cannot poison the cache for the right one. */
function checkShape(url: string, doc: unknown): TwinGeometry {
  const g = (doc ?? {}) as Partial<Record<keyof TwinGeometry, unknown>>;
  const version = typeof g.schema_version === 'string' ? g.schema_version : '';
  if (version.split('.')[0] !== String(GEOMETRY_SCHEMA_MAJOR)) {
    throw new GeometryMismatch(
      url,
      `schema ${version || '?'}, expected ${GEOMETRY_SCHEMA_MAJOR}.x`,
    );
  }
  if (g.units !== GEOMETRY_UNITS) {
    throw new GeometryMismatch(url, `units ${String(g.units)}, expected ${GEOMETRY_UNITS}`);
  }
  // The parts every reader dereferences. A 2.x file without them is not a
  // building this console can draw, and would otherwise throw mid-render.
  const provenance = g.generated_from as { map_version?: unknown } | undefined;
  const places = g.places_check as { levels?: unknown } | undefined;
  const missing = [
    !Array.isArray(g.levels) && 'levels',
    !Array.isArray(g.lifts) && 'lifts',
    (typeof places?.levels !== 'object' || places.levels === null) && 'places_check',
    typeof provenance?.map_version !== 'string' && 'generated_from.map_version',
  ].filter(Boolean);
  if (missing.length) throw new GeometryMismatch(url, `no ${missing.join(', ')}`);
  return doc as TwinGeometry;
}

/** One fetch per asset URL per session. Keyed by URL, not a singleton: a
 *  second site's asset is a second entry, never the first site's building. */
const cache = new Map<string, Promise<TwinGeometry>>();

/**
 * Load and validate the asset at `url` for `siteId`.
 *
 * Rejects with `GeometryMismatch` when the asset is not schema 2.x, not in
 * RMF frame units, or for another site. Any other failure (network, a non-2xx,
 * bad JSON) rejects with a plain Error and is not cached, so the next mount
 * tries again.
 */
export function loadGeometry(url: string, siteId: string): Promise<TwinGeometry> {
  let shaped = cache.get(url);
  if (!shaped) {
    shaped = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`geometry asset ${res.status}`);
        return res.json() as Promise<unknown>;
      })
      .then((doc) => checkShape(url, doc));
    cache.set(url, shaped);
    shaped.catch(() => {
      if (cache.get(url) === shaped) cache.delete(url);
    });
  }
  return shaped.then((g) => {
    if (g.site !== siteId) {
      throw new GeometryMismatch(url, `site ${String(g.site)}, expected ${siteId}`);
    }
    return g;
  });
}

export function _resetGeometryCacheForTest(): void {
  cache.clear();
}
